import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { FormEvent, ReactNode } from "react";
import { bindQuerySession, queryClient } from "./query-client";
import {
  bootstrapFailureTitle,
  isAccountSecurityRoute,
  requiresAuthenticator,
} from "./session-navigation";
import { EngineWorkbench } from "./components/EngineWorkbench";
import { Brand } from "./components/Brand";
import { W2ExtractionReview } from './components/W2ExtractionReview';
import { AuthenticatorEnrollment } from "./components/AuthenticatorEnrollment";
import {
  FirmSignup,
  isFirmSignupEnabled,
  legacyFirmSignupUrl,
} from "./components/FirmSignup";
import { readMfaEnrollment, mfaSetupError } from "./mfa-enrollment";
import type { MfaEnrollment } from "./mfa-enrollment";
import {
  Activity,
  ArrowDownToLine,
  ArrowRight,
  ArrowUpRight,
  Bell,
  BookOpen,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleHelp,
  ClipboardList,
  Command,
  Database,
  FileCheck2,
  FileText,
  Files,
  Fingerprint,
  Flag,
  FolderKanban,
  Globe,
  History,
  Layers3,
  LayoutDashboard,
  ListChecks,
  LockKeyhole,
  LogOut,
  Menu,
  MoreHorizontal,
  Network,
  PanelLeftClose,
  Pin,
  Play,
  Plus,
  Search,
  Settings2,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  UploadCloud,
  User,
  UserCheck,
  Users,
  Workflow,
  X,
} from "lucide-react";
import { modules, screens, publicEntryRoutes } from "./catalog";
import {
  RETURN_COPY,
  activityLabel,
  capabilityLabel,
  createReturnDisplayIndex,
  displayReturnId,
  returnSystemText,
  taxText,
} from "./terminology";
import type { ReturnDisplayIndex } from "./terminology";
import {
  AdminDirectory,
  PersonalTools,
  applyWorkspacePreferences,
} from "./components/WorkspaceTools";
import type { Screen } from "./catalog";
import {
  api,
  ApiError,
  downloadJson,
  patch,
  post,
  remove,
  setCsrf,
} from "./api";
import type { Row } from "./api";
import {
  Badge,
  Button,
  Callout,
  Empty,
  KeyValue,
  MiniIcon,
  Modal,
  Progress,
  SectionTitle,
  Skeleton,
} from "./ui";
import { DataTable } from "./components/DataTable";
import { CaseAssignments } from "./components/CaseAssignments";
import { IntakeExperience } from "./components/IntakeExperience";
import { ReturnBuilder, initialBuilderStage } from "./components/ReturnBuilder";
import {
  useFormValidation,
  useInteractiveFormValidation,
  validateFormFields,
  type ValidationField,
} from "./form-validation";
import {
  FieldError,
  ValidationSummary,
  focusValidationIssue,
} from "./components/ValidationSummary";
import {
  PreparationOverview,
  IntakeReview,
  PreparationRecordSummary,
  FormPreparationStatus,
  InterviewJourney,
} from "./components/PreparationViews";
import {
  canUseTechnicalDetails,
  isTechnicalScreen,
  preparationNavigation,
  preparationError,
  notesForReturn,
  isDocumentActivity,
} from "./presentation";
import { AuditEvents, NotificationCenter } from "./components/OperationalViews";
import { ProfessionalOperations } from "./components/ProfessionalOperations";
import { PortalInvitation } from "./components/PortalInvitation";
import {
  operationsEnabled,
  operationsModule,
  portalModule,
} from "./professional-operations";
import {
  CoveragePanel,
  FormMapView,
  ManifestView,
  RuleGraph,
} from "./components/DomainViews";
import {
  canCapture,
  parseDraftValue,
  serializeDraftValue,
  structuredCapture,
} from "./capture";

const icons: Record<string, any> = {
  LayoutDashboard,
  ClipboardList,
  Files,
  Network,
  Workflow,
  Play,
  ShieldCheck,
  UserCheck,
  FileText,
  Award: Shield,
  Rocket: Layers3,
  History,
  Settings: Settings2,
  Settings2,
  Command,
  ListTodo: ListChecks,
  LockKeyhole,
  Shield,
  Users,
  Database,
  Layers: Layers3,
  Activity,
  FolderKanban,
  ListChecks,
};
const blankWorkspace = {
  cases: [],
  activity: [],
  notifications: [],
  tasks: [],
  pins: [],
  views: [],
  capabilities: {},
};
type Context = {
  session: Row;
  workspace: Row;
  catalog: Row;
  selected: Row | null;
  selectCase: (id: string) => boolean;
  refresh: () => Promise<void>;
  navigate: (path: string) => void;
  inspect: (row: Row) => void;
  notify: (message: string) => void;
  returnDisplayIds: ReturnDisplayIndex;
  technical: boolean;
  technicalDetails?: (row: Row) => void;
};
const WorkspaceContext = createContext<Context>(null!);
const useWorkspace = () => useContext(WorkspaceContext);
const human = (s: unknown) =>
  taxText(
    String(s ?? "")
      .replaceAll("_", " ")
      .replaceAll("-", " ")
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase()),
  );
const dateLabel = (d: unknown) =>
  d
    ? new Date(String(d)).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "—";
const idOf = (r: Row) => String(r.key || r.id || r.name || r.reference);
const topicPreparationPaths = new Set([
  "/intake/questionnaire",
  "/intake/facts",
  "/intake/supplemental",
  "/intake/w2-upload",
  "/intake/reconciliation",
  "/intake/admission",
  "/forms/1040",
]);
export function authValidationFields(
  mode: string,
  values: Row,
  useRecovery: boolean,
): ValidationField[] {
  const fields: ValidationField[] = [];
  if (mode === "setup")
    fields.push({
      id: "auth-name",
      label: "Full name",
      value: values.name,
      required: true,
      minLength: 2,
      maxLength: 100,
    });
  fields.push({
    id: "auth-email",
    label: "Email address",
    value: values.email,
    required: true,
    maxLength: 254,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    patternMessage:
      "Enter an email address with a name, @ symbol and domain, such as name@example.com.",
  });
  if (mode !== "reset")
    fields.push({
      id: "auth-password",
      label: "Password",
      value: values.password,
      required: true,
      minLength: mode === "setup" ? 12 : 1,
      maxLength: 256,
    });
  if (mode === "login")
    fields.push(
      useRecovery
        ? {
            id: "auth-recovery-code",
            label: "Recovery code",
            value: values.recoveryCode,
            maxLength: 80,
          }
        : {
            id: "auth-authenticator-code",
            label: "Authenticator code",
            value: values.totp,
            pattern: /^[0-9]{6}$/,
            patternMessage:
              "Enter all 6 digits from the authenticator, or leave this field empty when an authenticator is not enabled.",
          },
    );
  return fields;
}
export function createReturnValidationFields(
  name: string,
  consent: boolean,
): ValidationField[] {
  return [
    {
      id: "create-return-name",
      label: "Return name",
      value: name,
      required: true,
      minLength: 2,
      maxLength: 120,
    },
    {
      id: "create-return-synthetic-consent",
      label: "Synthetic-data confirmation",
      value: consent ? true : undefined,
      required: true,
      requiredMessage:
        "Confirm that this return contains synthetic data only. Real taxpayer data is not supported.",
    },
  ];
}
export function taskValidationFields(
  notes: boolean,
  title: string,
  body: string,
): ValidationField[] {
  return [
    {
      id: notes ? "review-note-title" : "task-title",
      label: notes ? "Note title" : "Task title",
      value: title,
      required: true,
      maxLength: 180,
    },
    ...(notes
      ? [
          {
            id: "review-note-details",
            label: "Details",
            value: body,
            maxLength: 8000,
          },
        ]
      : []),
  ];
}
export function securityValidationFields(
  enrolling: boolean,
  canEnroll: boolean,
  password: string,
  code: string,
): ValidationField[] {
  return [
    ...(canEnroll
      ? [
          {
            id: "security-current-password",
            label: "Current password",
            value: password,
            required: true,
            maxLength: 256,
          },
        ]
      : []),
    ...(enrolling
      ? [
          {
            id: "security-authenticator-code",
            label: "Authenticator code",
            value: code,
            required: true,
            pattern: /^[0-9]{6}$/,
            patternMessage:
              "Enter exactly 6 digits from the authenticator app.",
          },
        ]
      : []),
  ];
}
export function evidenceValidationFields(
  files: { name: string; type: string; size: number }[],
): ValidationField[] {
  return [
    {
      id: "evidence-documents",
      label: "Documents",
      value: files,
      validate: () => {
        const errors: string[] = [];
        for (const file of files) {
          const name = file.name || "Unnamed document";
          if (
            !file.name.trim() ||
            file.name.length > 180 ||
            /[\x00-\x1f\\/]/.test(file.name)
          )
            errors.push(
              `“${name}”: use a plain file name between 1 and 180 characters, without folder separators or control characters.`,
            );
          if (!Number.isFinite(file.size) || file.size <= 0)
            errors.push(
              `“${name}”: the document is empty or its size is unavailable. Select a nonempty document.`,
            );
          else if (file.size > 5 * 1024 * 1024)
            errors.push(
              `“${name}”: the document exceeds 5 MB. Select a document of 5 MB or smaller.`,
            );
          if (
            ![
              "application/pdf",
              "image/png",
              "image/jpeg",
              "text/plain",
            ].includes(file.type || "text/plain")
          )
            errors.push(
              `“${name}”: this file type is not supported. Select a PDF, PNG, JPG or TXT document.`,
            );
        }
        return errors.length ? errors.join(" ") : undefined;
      },
    },
  ];
}
export default function App() {
  const [session, setSession] = useState<Row | null>(null),
    [workspace, setWorkspace] = useState<Row>(blankWorkspace),
    [catalog, setCatalog] = useState<Row>({}),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [errorCode, setErrorCode] = useState("");
  const [route, setRoute] = useState(location.pathname),
    [selectedId, setSelectedId] = useState(""),
    [command, setCommand] = useState(false),
    [search, setSearch] = useState(""),
    [inspect, setInspect] = useState<Row | null>(null),
    [toast, setToast] = useState(""),
    [mobile, setMobile] = useState(false),
    [notifications, setNotifications] = useState(false),
    [technicalRequested, setTechnicalRequested] = useState(false),
    [inspectTechnical, setInspectTechnical] = useState(false);
  const catalogCache = useRef<Row | null>(null);
  const refreshGeneration = useRef(0);
  const sidebarRef = useRef<HTMLElement>(null);
  const intakeDirty = useRef(false);
  const activeRoute = useRef(location.pathname);
  const historyIndex = useRef(0);
  const restoringHistory = useRef(false);
  const accountSecurityRoute = isAccountSecurityRoute(route);
  useEffect(() => {
    history.replaceState({ ...history.state, ztInterviewIndex: 0 }, "");
    const mark = (event: Event) => {
      intakeDirty.current = Boolean((event as CustomEvent).detail?.dirty);
    };
    const unload = (event: BeforeUnloadEvent) => {
      if (intakeDirty.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("zt:intake-dirty", mark);
    window.addEventListener("beforeunload", unload);
    return () => {
      window.removeEventListener("zt:intake-dirty", mark);
      window.removeEventListener("beforeunload", unload);
    };
  }, []);
  const navigate = useCallback((path: string) => {
    if (
      intakeDirty.current &&
      path !== location.pathname &&
      !window.confirm(
        "You have an unsaved answer. Leave this step and discard those changes?",
      )
    )
      return;
    if (path !== location.pathname) intakeDirty.current = false;
    if (path !== location.pathname) {
      // Do not mount product screens with an empty security-only workspace.
      // Crossing this boundary re-loads the session and server entitlements.
      if (
        isAccountSecurityRoute(path) !==
        isAccountSecurityRoute(location.pathname)
      )
        setLoading(true);
      historyIndex.current++;
      history.pushState({ ztInterviewIndex: historyIndex.current }, "", path);
    }
    activeRoute.current = path;
    setRoute(path);
    setMobile(false);
    window.scrollTo({ top: 0 });
  }, []);
  const refresh = useCallback(async () => {
    const generation = ++refreshGeneration.current;
    const s = await api("/session");
    if (generation !== refreshGeneration.current) return;
    const scopeChanged = bindQuerySession(s.user?.id, s.workspace?.id);
    if (scopeChanged) {
      // Fail closed while a different principal/tenant loads. Query cache
      // clearing alone leaves active observers able to display old results.
      setSession(null);
      setWorkspace(blankWorkspace);
      setCatalog({});
      setSelectedId("");
      setInspect(null);
      setCommand(false);
      setNotifications(false);
      setTechnicalRequested(false);
      setInspectTechnical(false);
      setError("");
      setErrorCode("");
      setLoading(true);
    }
    setCsrf(s.csrfToken);
    try {
      if (
        s.user &&
        !requiresAuthenticator(s) &&
        !isAccountSecurityRoute(activeRoute.current)
      ) {
        const [w, c, preferences] = await Promise.all([
          api("/workspace"),
          catalogCache.current
            ? Promise.resolve(catalogCache.current)
            : api("/catalog"),
          api("/preferences"),
        ]);
        if (generation !== refreshGeneration.current) return;
        applyWorkspacePreferences(preferences);
        catalogCache.current = c;
        setWorkspace(w);
        setCatalog(c);
      } else {
        // Account security must not depend on product licensing. In particular,
        // an unenrolled session cannot fetch product data before MFA setup.
        queryClient.clear();
        catalogCache.current = null;
        setWorkspace(blankWorkspace);
        setCatalog({});
        setInspect(null);
        setSelectedId("");
        setTechnicalRequested(false);
        setInspectTechnical(false);
        setCommand(false);
        setNotifications(false);
      }
      setError("");
      setErrorCode("");
      setSession(s);
      if (requiresAuthenticator(s) && activeRoute.current !== "/auth/mfa")
        navigate("/auth/mfa");
    } catch (failure) {
      if (scopeChanged && generation === refreshGeneration.current) {
        setError(
          "The signed-in workspace could not be loaded. Retry to continue; the previous workspace has been closed.",
        );
      }
      throw failure;
    } finally {
      if (scopeChanged && generation === refreshGeneration.current)
        setLoading(false);
    }
  }, [navigate]);
  useEffect(() => {
    const expired = () => {
      refreshGeneration.current++;
      bindQuerySession();
      setLoading(false);
      catalogCache.current = null;
      setCsrf();
      setWorkspace(blankWorkspace);
      setCatalog({});
      setSelectedId("");
      setInspect(null);
      setCommand(false);
      setNotifications(false);
      setTechnicalRequested(false);
      setInspectTechnical(false);
      setError("");
      setErrorCode("");
      setSession({ user: null, setupRequired: false, expired: true });
    };
    window.addEventListener("zt:session-expired", expired);
    return () => window.removeEventListener("zt:session-expired", expired);
  }, []);
  useEffect(() => {
    let current = true;
    setLoading(true);
    refresh()
      .catch((e) => {
        if (
          current &&
          !(e instanceof ApiError && e.code === "AUTHENTICATION_REQUIRED")
        ) {
          setErrorCode(e instanceof ApiError ? e.code : "");
          setError(e.message);
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    const pop = (event: PopStateEvent) => {
      if (restoringHistory.current) {
        restoringHistory.current = false;
        return;
      }
      const nextIndex = event.state?.ztInterviewIndex;
      if (
        intakeDirty.current &&
        location.pathname !== activeRoute.current &&
        !window.confirm(
          "You have an unsaved answer. Leave this step and discard those changes?",
        )
      ) {
        if (Number.isInteger(nextIndex) && nextIndex !== historyIndex.current) {
          restoringHistory.current = true;
          history.go(historyIndex.current - nextIndex);
        } else {
          history.pushState(
            { ztInterviewIndex: historyIndex.current },
            "",
            activeRoute.current,
          );
        }
        return;
      }
      if (location.pathname !== activeRoute.current)
        intakeDirty.current = false;
      if (Number.isInteger(nextIndex)) historyIndex.current = nextIndex;
      activeRoute.current = location.pathname;
      if (isAccountSecurityRoute(location.pathname) !== accountSecurityRoute)
        setLoading(true);
      setRoute(location.pathname);
    };
    window.addEventListener("popstate", pop);
    return () => {
      current = false;
      window.removeEventListener("popstate", pop);
    };
  }, [refresh, accountSecurityRoute]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommand((c) => !c);
      }
      if (e.key === "Escape") setMobile(false);
      if (
        e.key.toLowerCase() === "n" &&
        !(
          e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement
        ) &&
        !e.ctrlKey &&
        !e.metaKey
      )
        navigate("/intake/new");
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [navigate]);
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(""), 5000);
      return () => clearTimeout(t);
    }
  }, [toast]);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 740px)");
    const changed = () => {
      if (!media.matches) setMobile(false);
    };
    media.addEventListener("change", changed);
    return () => media.removeEventListener("change", changed);
  }, []);
  useEffect(() => {
    if (!mobile || !sidebarRef.current) return;
    const panel = sidebarRef.current;
    const previous = document.activeElement as HTMLElement | null;
    const focusFrame = window.setTimeout(
      () =>
        panel
          .querySelector<HTMLButtonElement>(
            'button[aria-label="Close navigation"]',
          )
          ?.focus({ preventScroll: true }),
      0,
    );
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const trap = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const nodes = [
        ...panel.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex="0"]',
        ),
      ].filter((node) => node.getClientRects().length);
      const first = nodes[0],
        last = nodes.at(-1);
      if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", trap);
    return () => {
      window.clearTimeout(focusFrame);
      document.removeEventListener("keydown", trap);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [mobile]);
  const selected =
    workspace.cases?.find((c: Row) => c.id === selectedId) ||
    workspace.cases?.[0] ||
    null;
  const returnDisplayIds = useMemo(
    () => createReturnDisplayIndex(workspace.cases || []),
    [workspace.cases],
  );
  const isClientPortal = session?.user?.role === "taxpayer";
  const operationsActive = operationsEnabled(workspace);
  const operationScreen = [
    ...operationsModule.screens,
    ...portalModule.screens,
  ].find((item) => item.path === route);
  const screen = requiresAuthenticator(session)
    ? screens.find((s) => s.path === "/auth/mfa")!
    : isClientPortal && !route.startsWith("/auth/")
      ? portalModule.screens[0]
      : operationScreen ||
        screens.find((s) => s.path === route) ||
        screens.find((s) => s.kind === "dashboard")!;
  const module = [...modules, operationsModule, portalModule].find(
    (m) => m.id === screen.module,
  )!;
  const technicalAllowed = canUseTechnicalDetails(session?.user?.role);
  const technical = technicalAllowed && technicalRequested;
  const preparationModules = preparationNavigation(
    modules,
    session?.user?.role,
    technical,
  );
  const visibleModules = isClientPortal
    ? [portalModule, ...preparationModules.filter((item) => item.id === "auth")]
    : [...preparationModules, ...(operationsActive ? [operationsModule] : [])];
  const technicalRouteBlocked =
    screen.kind !== "professional-operations" &&
    !technical &&
    isTechnicalScreen(screen);
  useEffect(() => {
    if (!technicalAllowed) {
      setTechnicalRequested(false);
      setInspectTechnical(false);
      setInspect((current) => (current?.source ? null : current));
    }
  }, [technicalAllowed]);
  const notify = useCallback((m: string) => setToast(taxText(m)), []);
  const closeInspect = useCallback(() => setInspect(null), []),
    closeCommand = useCallback(() => {
      setCommand(false);
      setSearch("");
    }, []),
    closeNotifications = useCallback(() => setNotifications(false), []);
  const openRecord = useCallback(
    (row: Row, explicitTechnical = false) => {
      if (row.source && !technical && !(technicalAllowed && explicitTechnical))
        return;
      setInspectTechnical(technicalAllowed && (technical || explicitTechnical));
      setInspect(row);
      if (row.key && row.source)
        api("/catalog/record?key=" + encodeURIComponent(row.key))
          .then((full) =>
            setInspect((current) =>
              current?.key === row.key ? full : current,
            ),
          )
          .catch((e) => notify(e.message));
    },
    [notify, technical, technicalAllowed],
  );
  if (loading)
    return (
      <div className="boot">
        <Brand />
        <div className="boot-line" />
        <span>Opening your precision workspace</span>
      </div>
    );
  if (error)
    return (
      <main className="fatal">
        <Brand />
        <h1>{bootstrapFailureTitle(errorCode)}</h1>
        <p>{taxText(error)}</p>
        <Button variant="primary" onClick={() => location.reload()}>
          Try again
        </Button>
        {errorCode.startsWith("LICENSE_") && (
          <Button onClick={() => navigate("/auth/mfa")}>
            Account security
          </Button>
        )}
      </main>
    );
  if (route === publicEntryRoutes.portalInvitation)
    return (
      <PortalInvitation
        complete={() => {
          navigate("/auth/login");
          void refresh();
        }}
      />
    );
  if (!session?.user)
    return (
      <Auth
        session={session || {}}
        complete={refresh}
        route={route}
        navigate={navigate}
      />
    );
  const ctx: Context = {
    session,
    workspace: technical
      ? workspace
      : {
          ...workspace,
          activity: (workspace.activity || []).filter(
            (event: Row) => event.type !== "engine_mechanics",
          ),
        },
    catalog,
    selected,
    selectCase: (id) => {
      if (
        id !== selectedId &&
        intakeDirty.current &&
        !window.confirm(
          "You have an unsaved answer. Switch returns and discard those changes?",
        )
      )
        return false;
      intakeDirty.current = false;
      setSelectedId(id);
      return true;
    },
    refresh,
    navigate,
    inspect: openRecord,
    notify,
    returnDisplayIds,
    technical,
    technicalDetails: technicalAllowed
      ? (row) => openRecord(row, true)
      : undefined,
  };
  const searchResults = visibleModules
    .flatMap((m) => m.screens)
    .filter((s) =>
      `${s.title} ${s.module}`.toLowerCase().includes(search.toLowerCase()),
    );
  const records =
    search.length > 1
      ? [
          ...workspace.cases,
          ...(technical ? catalog.rules || [] : []),
          ...(technical ? catalog.facts || [] : []),
        ]
          .filter((r) =>
            `${r.name} ${r.source ? taxText(r.name) : ""} ${r.id} ${r.reference || ""} ${returnDisplayIds.get(String(r.id)) || ""}`
              .toLowerCase()
              .includes(search.toLowerCase()),
          )
          .slice(0, 8)
      : [];
  return (
    <WorkspaceContext.Provider value={ctx}>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <div className="app-layout">
        {mobile && (
          <div className="mobile-scrim" onClick={() => setMobile(false)} />
        )}
        <aside
          ref={sidebarRef}
          id="primary-navigation"
          className={"sidebar" + (mobile ? " mobile-open" : "")}
          aria-label="Main navigation"
          role={mobile ? "dialog" : undefined}
          aria-modal={mobile ? true : undefined}
          inert={command || Boolean(inspect) || notifications}
          onTransitionEnd={(event) => {
            if (
              mobile &&
              event.target === event.currentTarget &&
              !event.currentTarget.contains(document.activeElement)
            )
              event.currentTarget
                .querySelector<HTMLButtonElement>(
                  'button[aria-label="Close navigation"]',
                )
                ?.focus({ preventScroll: true });
          }}
        >
          <div className="sidebar-brand">
            <Brand />
            <button
              className="icon-btn mobile-only"
              onClick={() => setMobile(false)}
              aria-label="Close navigation"
            >
              <X size={18} />
            </button>
          </div>
          <button
            className="workspace-switch"
            onClick={() =>
              navigate(technical ? "/admin/tenants" : "/workspace/dashboard")
            }
          >
            <span className="workspace-avatar">Z</span>
            <span>
              <strong>
                {session.workspace?.name || "ZentroTax workspace"}
              </strong>
              <small>Synthetic environment</small>
            </span>
            <ChevronDown size={14} />
          </button>
          <button className="side-search" onClick={() => setCommand(true)}>
            <Search size={15} />
            <span>Search anything</span>
            <kbd>⌘ K</kbd>
          </button>
          <div className="nav-heading">WORKSPACE</div>
          <nav className="navigation">
            {visibleModules.map((m, index) => {
              const Icon = icons[m.icon] || Layers3;
              const active = m.id === module.id;
              return (
                <div key={m.id} className="nav-group">
                  {technical && index === 4 && (
                    <div className="nav-heading">PREPARATION ENGINE</div>
                  )}
                  {technical && index === 11 && (
                    <div className="nav-heading">GOVERNANCE</div>
                  )}
                  <a
                    href={
                      !technical && m.id === "intake"
                        ? "/intake/questionnaire"
                        : m.screens[0].path
                    }
                    className={"nav-link" + (active ? " active" : "")}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(
                        !technical && m.id === "intake"
                          ? "/intake/questionnaire"
                          : m.screens[0].path,
                      );
                    }}
                  >
                    <Icon size={17} />
                    <span>
                      {!technical && m.id === "intake"
                        ? "Return Builder"
                        : !technical && m.id === "evidence"
                          ? "Documents"
                          : !technical && m.id === "forms"
                            ? "Federal Return"
                            : m.label}
                    </span>
                    {active && <span className="nav-active-dot" />}
                  </a>
                  {active &&
                    m.screens.length > 1 &&
                    (technical || m.id !== "intake") && (
                      <div className="subnav">
                        {m.screens.map((s) => (
                          <a
                            key={s.path}
                            href={s.path}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(s.path);
                            }}
                            aria-current={
                              s.path === screen.path ? "page" : undefined
                            }
                            className={s.path === screen.path ? "current" : ""}
                          >
                            {s.title}
                          </a>
                        ))}
                      </div>
                    )}
                </div>
              );
            })}
          </nav>
          <div className="sidebar-bottom">
            {technicalAllowed && (
              <Button
                onClick={() => {
                  setTechnicalRequested(!technical);
                  setInspect(null);
                  setInspectTechnical(false);
                  setCommand(false);
                  if (technical && isTechnicalScreen(screen))
                    navigate("/intake/questionnaire");
                }}
              >
                {technical ? "Back to preparation" : "Technical Details"}
              </Button>
            )}
            <div className="environment-label">
              <span className="status-dot" />
              Local workspace <span>TY 2025</span>
            </div>
            <button
              className="profile-button"
              onClick={() => navigate("/auth/sessions")}
            >
              <span className="avatar">
                {String(session.user.name)
                  .split(" ")
                  .map((w: string) => w[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <span>
                <strong>{session.user.name}</strong>
                <small>{human(session.user.role)}</small>
              </span>
              <Settings2 size={15} />
            </button>
          </div>
        </aside>
        <div
          className="main-shell"
          inert={mobile || command || Boolean(inspect) || notifications}
        >
          <header className="topbar">
            <div className="breadcrumb">
              <button
                className="icon-btn mobile-only"
                onClick={() => setMobile(true)}
                aria-label="Open navigation"
                aria-expanded={mobile}
                aria-controls="primary-navigation"
              >
                <Menu size={20} />
              </button>
              <span>ZentroTax</span>
              <ChevronRight size={13} />
              <span>
                {technicalRouteBlocked
                  ? "Preparation"
                  : !technical && module.id === "intake"
                    ? "Return Builder"
                    : module.label}
              </span>
              <ChevronRight size={13} />
              <strong>
                {technicalRouteBlocked ? "Preparation workspace" : screen.title}
              </strong>
            </div>
            <div className="topbar-actions">
              <span className="release-label">
                <span />
                FED · TY2025
              </span>
              <button
                className="icon-btn"
                aria-label="Open command palette"
                onClick={() => setCommand(true)}
              >
                <Search size={18} />
              </button>
              <button
                className="icon-btn notification-icon"
                aria-label="Notifications"
                onClick={() => setNotifications(true)}
              >
                <Bell size={18} />
                {workspace.notifications?.some((n: Row) => !n.read) && <i />}
              </button>
              <span className="topbar-divider" />
              <button
                className="icon-btn"
                title="Sign out"
                aria-label="Sign out"
                onClick={async () => {
                  try {
                    await post("/auth/logout");
                    setCsrf();
                    setWorkspace(blankWorkspace);
                    setCatalog({});
                    setSelectedId("");
                    setInspect(null);
                    await refresh();
                  } catch (e) {
                    notify((e as Error).message);
                  }
                }}
              >
                <LogOut size={17} />
              </button>
            </div>
          </header>
          <main
            id="main-content"
            className="main-content"
            tabIndex={-1}
            key={screen.id}
          >
            {technical && (
              <Callout title="Technical Details · Administrator view">
                System records shown here are for troubleshooting.{" "}
                <button
                  className="text-button"
                  onClick={() => {
                    setTechnicalRequested(false);
                    setInspect(null);
                    setInspectTechnical(false);
                    navigate("/intake/questionnaire");
                  }}
                >
                  Back to preparation
                </button>
              </Callout>
            )}
            {screen.kind === "professional-operations" ? (
              <ProfessionalOperations
                key={`${session.user.id}:${session.workspace?.id || ""}`}
                enabled={operationsActive}
                role={session.user.role}
                sessionScope={`${session.user.id}:${session.workspace?.id || ""}`}
                navigate={navigate}
                mfaRequired={workspace.mfaRequired === true}
                mfaEnabled={session.user.mfaEnabled}
              />
            ) : screen.kind === "dashboard" ? (
              <Dashboard />
            ) : (
              <>
                {!technical && topicPreparationPaths.has(screen.path) ? (
                  <div className="tax-interview-page-heading">
                    <h1>Return Builder</h1>
                    <p>Everything you need to build your taxpayer's return.</p>
                  </div>
                ) : (
                  <SectionTitle
                    eyebrow={
                      technicalRouteBlocked
                        ? "Preparation"
                        : !technical && module.id === "intake"
                          ? "Return Builder"
                          : module.label
                    }
                    title={
                      technicalRouteBlocked
                        ? "Preparation workspace"
                        : screen.title
                    }
                    description={
                      technicalRouteBlocked
                        ? "Use the intake workspace to gather and review taxpayer information."
                        : screen.description
                    }
                    action={
                      ["cases", "assignments"].includes(screen.kind) &&
                      ["workspace_admin", "preparer"].includes(
                        session.user.role,
                      ) ? (
                        <Button
                          variant="primary"
                          onClick={() => navigate("/intake/new")}
                        >
                          <Plus size={16} />
                          {RETURN_COPY.new}
                        </Button>
                      ) : (
                        <Badge>Synthetic workspace</Badge>
                      )
                    }
                  />
                )}
                <ScreenView screen={screen} />
              </>
            )}
            <footer className="page-footer">
              <span>
                <ShieldCheck size={13} />
                Evidence-led. Human-reviewed.
              </span>
              <span>
                {technical
                  ? "FED-IND-TY2025-W2-S1"
                  : "Federal individual · 2025"}{" "}
                <i />
                No submission capability
              </span>
            </footer>
          </main>
        </div>
      </div>
      {command && (
        <Modal title="Go anywhere" close={closeCommand} wide>
          <div className="command-input">
            <Search size={20} />
            <input
              aria-label={
                technical
                  ? RETURN_COPY.search
                  : "Search returns and preparation screens"
              }
              placeholder={
                technical
                  ? RETURN_COPY.searchPlaceholder
                  : "Find a return or preparation step…"
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <kbd>ESC</kbd>
          </div>
          <div className="command-results">
            <p className="eyebrow">
              {search ? "Matching screens" : "Jump to a workspace"}
            </p>
            {searchResults.slice(0, search ? 12 : 8).map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  navigate(s.path);
                  closeCommand();
                }}
              >
                <span className="mini-icon">
                  <Layers3 size={17} />
                </span>
                <span>
                  <strong>{s.title}</strong>
                  <small>{human(s.module)}</small>
                </span>
                <ArrowUpRight size={15} />
              </button>
            ))}
            {records.length > 0 && <p className="eyebrow">Records</p>}
            {records.map((r) => (
              <button
                key={idOf(r)}
                onClick={() => {
                  closeCommand();
                  openRecord(r);
                }}
              >
                <FileText size={17} />
                <span>
                  <strong>{r.source ? taxText(r.name) : r.name}</strong>
                  <small>
                    {returnDisplayIds.has(String(r.id))
                      ? displayReturnId(r, returnDisplayIds)
                      : r.id || r.reference}
                  </small>
                </span>
                <ArrowUpRight size={15} />
              </button>
            ))}
            {!searchResults.length && !records.length && (
              <Empty
                title="No matching records"
                description={
                  technical
                    ? "Try a return name, Return ID, rule identifier, or module."
                    : "Try a return name, Return ID, or preparation step."
                }
              />
            )}
          </div>
          <div className="command-footer">
            <span>Tab to navigate · Enter to open</span>
            <span>
              <Command size={13} />K anywhere
            </span>
          </div>
        </Modal>
      )}
      {inspect && (
        <Modal
          title={
            !inspectTechnical
              ? returnDisplayIds.has(String(inspect.id))
                ? RETURN_COPY.details
                : inspect.name || inspect.title || "Record overview"
              : returnDisplayIds.has(String(inspect.id))
                ? RETURN_COPY.details
                : inspect.source
                  ? taxText(
                      inspect.name ||
                        inspect.title ||
                        inspect.id ||
                        "Record details",
                    )
                  : inspect.name ||
                    inspect.title ||
                    inspect.id ||
                    "Record details"
          }
          close={closeInspect}
          wide
        >
          {inspectTechnical && technicalAllowed ? (
            <div className="detail-body">
              <Badge>Technical Details · Administrator view</Badge>
              <div className="detail-status">
                <Badge>{human(inspect.status || "Source definition")}</Badge>
                {inspect.id && (
                  <code>
                    {returnDisplayIds.has(String(inspect.id))
                      ? displayReturnId(inspect, returnDisplayIds)
                      : inspect.id}
                  </code>
                )}
              </div>
              {inspect.description && (
                <p className="detail-description">
                  {inspect.source
                    ? taxText(inspect.description)
                    : inspect.description}
                </p>
              )}
              {inspect.source && (
                <div className="source-card">
                  <BookOpen size={18} />
                  <div>
                    <strong>Original architecture source</strong>
                    <code>
                      {inspect.source.path}
                      {inspect.source.pointer}
                    </code>
                    <small>SHA-256 · {inspect.source.sha256}</small>
                  </div>
                </div>
              )}
              <pre className="json-view">
                {JSON.stringify(inspect.detail || inspect, null, 2)}
              </pre>
              <div className="form-actions">
                <Button
                  onClick={() => downloadJson("zentrotax-record.json", inspect)}
                >
                  <ArrowDownToLine size={15} />
                  Export record
                </Button>
                <Button variant="primary" onClick={closeInspect}>
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <>
              <PreparationRecordSummary
                record={inspect}
                returnId={returnDisplayIds.get(String(inspect.id))}
              />
              <div className="detail-body form-actions">
                {technicalAllowed && (
                  <Button onClick={() => openRecord(inspect, true)}>
                    Technical Details
                  </Button>
                )}
                <Button variant="primary" onClick={closeInspect}>
                  Done
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}
      {notifications && (
        <Modal title="Notification center" close={closeNotifications} wide>
          <div className="detail-body">
            <NotificationCenter
              technical={technical}
              records={workspace.notifications || []}
              returnRecords={workspace.cases || []}
              returnDisplayIds={returnDisplayIds}
              refresh={refresh}
              notify={notify}
            />
          </div>
        </Modal>
      )}
      {toast && (
        <div className="toast" role="status">
          <Check size={17} />
          <span>{toast}</span>
          <button
            aria-label="Dismiss notification"
            onClick={() => setToast("")}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </WorkspaceContext.Provider>
  );
}

function Auth({
  session,
  complete,
  route,
  navigate,
}: {
  session: Row;
  complete: () => Promise<void>;
  route: string;
  navigate: (path: string) => void;
}) {
  const [mode, setMode] = useState(session.setupRequired ? "setup" : "login"),
    [busy, setBusy] = useState(false),
    [useRecovery, setUseRecovery] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [authValues, setAuthValues] = useState<Row>({
      name: "",
      email: "",
      password: "",
      totp: "",
      recoveryCode: "",
    });
  const authSection =
    mode === "setup"
      ? "Workspace setup"
      : mode === "reset"
        ? "Password recovery"
        : "Sign in";
  const legacySignupUrl = legacyFirmSignupUrl(
    session.mode,
    window.location.origin,
  );
  const validation = useInteractiveFormValidation(
    authSection,
    authValidationFields(mode, authValues, useRecovery),
    `${mode}:${useRecovery ? "recovery" : "totp"}:${route}`,
  );
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy || !validation.validate()) return;
    setBusy(true);
    setError("");
    setNotice("");
    const values = Object.fromEntries(new FormData(e.currentTarget));
    try {
      if (mode === "reset") {
        await post("/auth/password-reset", values);
        setNotice(
          "If reset delivery is configured, instructions will be sent.",
        );
      } else {
        const signedIn = await post<Row>("/auth/" + mode, values);
        await complete();
        if (
          mode === "login" &&
          signedIn.user?.mfaEnabled === false &&
          session.registration?.mfaRequired === true
        )
          navigate("/auth/mfa");
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="auth-layout">
      <section className="auth-story">
        <Brand />
        <div className="auth-story-main">
          <div className="eyebrow">THE PRECISION WORKSPACE</div>
          <h1>
            Every detail.
            <br />
            Every decision.
            <br />
            <em>Accounted for.</em>
          </h1>
          <p>
            A connected workspace for the evidence, intelligence, and people
            behind every tax return.
          </p>
          <div className="auth-orbit">
            <div className="orbit-ring ring-one" />
            <div className="orbit-ring ring-two" />
            <div className="orbit-core">
              <ShieldCheck size={44} strokeWidth={1.3} />
            </div>
            <span className="orbit-label label-one">
              <Fingerprint size={15} />
              Traceable by design
            </span>
            <span className="orbit-label label-two">
              <Network size={15} />
              Connected intelligence
            </span>
            <span className="orbit-label label-three">
              <UserCheck size={15} />
              Human judgment
            </span>
          </div>
        </div>
        <div className="auth-foot">
          ZentroTax <span>FED · TY2025 · W2-S1</span>
        </div>
      </section>
      <section className="auth-form-side">
        <span className="auth-top-note">
          <LockKeyhole size={14} />
          Private local workspace
        </span>
        <div className="auth-form-wrap">
          <Brand className="auth-mobile-brand" />
          {route === publicEntryRoutes.firmSignup ? (
            <FirmSignup
              registration={session.registration}
              onSignIn={(email) => {
                validation.reset();
                setAuthValues({
                  name: "",
                  email: email || "",
                  password: "",
                  totp: "",
                  recoveryCode: "",
                });
                setMode("login");
                setUseRecovery(false);
                setError("");
                setNotice("");
                navigate("/auth/login");
              }}
            />
          ) : (
            <>
              <Badge>Built for certainty</Badge>
              <h2>
                {mode === "setup"
                  ? "Make room for clarity."
                  : mode === "reset"
                    ? "Let’s get you back in."
                    : "Welcome back."}
              </h2>
              <p>
                {mode === "setup"
                  ? "Create the first administrator for this local synthetic workspace."
                  : mode === "reset"
                    ? "Password recovery requires a configured delivery service."
                    : "Sign in to your ZentroTax workspace."}
              </p>
              {session.expired && (
                <Callout title="Your session has ended">
                  Sign in again to continue. Previously saved records are
                  retained; the interrupted action was not confirmed.
                </Callout>
              )}
              <form onSubmit={submit} noValidate>
                <ValidationSummary issues={validation.issues} />
                {mode === "setup" && (
                  <label>
                    Full name
                    <input
                      {...validation.fieldProps("auth-name")}
                      name="name"
                      value={authValues.name}
                      onChange={(event) =>
                        setAuthValues((previous) => ({
                          ...previous,
                          name: event.target.value,
                        }))
                      }
                      placeholder="Your name"
                      required
                      autoComplete="name"
                      minLength={2}
                      maxLength={100}
                    />
                    <FieldError issue={validation.issue("auth-name")} />
                  </label>
                )}
                <label>
                  Email address
                  <input
                    {...validation.fieldProps("auth-email")}
                    name="email"
                    value={authValues.email}
                    onChange={(event) =>
                      setAuthValues((previous) => ({
                        ...previous,
                        email: event.target.value,
                      }))
                    }
                    type="email"
                    placeholder="you@company.com"
                    required
                    autoComplete="username"
                    maxLength={254}
                  />
                  <FieldError issue={validation.issue("auth-email")} />
                </label>
                {mode !== "reset" && (
                  <label>
                    Password
                    <input
                      {...validation.fieldProps("auth-password")}
                      name="password"
                      value={authValues.password}
                      onChange={(event) =>
                        setAuthValues((previous) => ({
                          ...previous,
                          password: event.target.value,
                        }))
                      }
                      type="password"
                      placeholder={
                        mode === "setup"
                          ? "At least 12 characters"
                          : "Enter your password"
                      }
                      required
                      minLength={mode === "setup" ? 12 : 1}
                      maxLength={256}
                      autoComplete={
                        mode === "setup" ? "new-password" : "current-password"
                      }
                    />
                    <FieldError issue={validation.issue("auth-password")} />
                  </label>
                )}
                {mode === "login" && (
                  <>
                    <label>
                      {useRecovery ? "Recovery code" : "Authenticator code"}{" "}
                      <span className="label-optional">if enabled</span>
                      <input
                        {...validation.fieldProps(
                          useRecovery
                            ? "auth-recovery-code"
                            : "auth-authenticator-code",
                        )}
                        name={useRecovery ? "recoveryCode" : "totp"}
                        value={
                          useRecovery
                            ? authValues.recoveryCode
                            : authValues.totp
                        }
                        onChange={(event) =>
                          setAuthValues((previous) => ({
                            ...previous,
                            [useRecovery ? "recoveryCode" : "totp"]:
                              event.target.value,
                          }))
                        }
                        inputMode={useRecovery ? "text" : "numeric"}
                        autoComplete="one-time-code"
                        placeholder={
                          useRecovery
                            ? "One-time recovery code"
                            : "6-digit code"
                        }
                        maxLength={useRecovery ? 80 : 6}
                      />
                      <FieldError
                        issue={validation.issue(
                          useRecovery
                            ? "auth-recovery-code"
                            : "auth-authenticator-code",
                        )}
                      />
                    </label>
                    <button
                      className="text-button auth-reset"
                      type="button"
                      onClick={() => setUseRecovery((v) => !v)}
                    >
                      {useRecovery
                        ? "Use authenticator code"
                        : "Use a recovery code"}
                    </button>
                    <button
                      className="text-button auth-reset"
                      type="button"
                      onClick={() => {
                        setMode("reset");
                        setError("");
                        setNotice("");
                      }}
                    >
                      Forgot password?
                    </button>
                  </>
                )}
                {error && (
                  <div className="inline-error" role="alert">
                    {authSection} request could not be completed:{" "}
                    {taxText(error)}
                  </div>
                )}
                {notice && <p role="status">{notice}</p>}
                <Button type="submit" variant="primary full" disabled={busy}>
                  {busy
                    ? "Connecting…"
                    : mode === "setup"
                      ? "Create workspace"
                      : mode === "reset"
                        ? "Request reset"
                        : "Sign in"}
                  <ArrowRight size={16} />
                </Button>
              </form>
              {mode === "reset" && (
                <button
                  className="text-button"
                  onClick={() => {
                    setMode("login");
                    setNotice("");
                    setError("");
                  }}
                >
                  Back to sign in
                </button>
              )}
              {mode === "login" &&
                isFirmSignupEnabled(session.registration) && (
                  <button
                    type="button"
                    className="text-button"
                    disabled={busy}
                    onClick={() => {
                      setAuthValues({
                        name: "",
                        email: "",
                        password: "",
                        totp: "",
                        recoveryCode: "",
                      });
                      setError("");
                      setNotice("");
                      navigate(publicEntryRoutes.firmSignup);
                    }}
                  >
                    Create a firm account
                  </button>
                )}
              {mode === "login" &&
                !isFirmSignupEnabled(session.registration) &&
                legacySignupUrl && (
                  <p>
                    <a
                      className="text-button"
                      href={legacySignupUrl}
                      rel="noreferrer"
                    >
                      Create a firm account
                    </a>
                    <br />
                    Signup uses the local PostgreSQL workspace. Existing
                    accounts can continue signing in here.
                  </p>
                )}
              <p className="auth-disclaimer">
                Synthetic data only. No tax filing or production certification
                is enabled in this local application.
              </p>
            </>
          )}
        </div>
        <div className="auth-bottom">Precision. Transparency. Confidence.</div>
      </section>
    </div>
  );
}

function Dashboard() {
  const {
    workspace: w,
    session,
    catalog: c,
    navigate,
    inspect,
    technical,
  } = useWorkspace();
  const open = w.tasks?.filter((t: Row) => t.status !== "done") || [];
  if (!technical)
    return (
      <PreparationOverview
        user={session.user}
        workspace={w}
        navigate={navigate}
      >
        <CaseTable />
      </PreparationOverview>
    );
  return (
    <>
      <div className="dashboard-heading">
        <div>
          <p className="eyebrow">YOUR WORKSPACE, IN FOCUS</p>
          <h1>
            Good to see you, {String(session.user.name).split(" ")[0]}
            <span className="green-period">.</span>
          </h1>
          <p>A clear view of your work. A confident next step.</p>
        </div>
        <Button variant="primary" onClick={() => navigate("/intake/new")}>
          <Plus size={16} />
          {RETURN_COPY.new} <kbd>N</kbd>
        </Button>
      </div>
      <div className="release-banner">
        <div className="release-emblem">
          <Layers3 size={20} />
        </div>
        <div>
          <strong>One connected preparation workspace.</strong>
          <span>Federal individual · Tax year 2025 · W-2 vertical slice</span>
        </div>
        <div className="banner-right">
          <Badge>Local · Synthetic</Badge>
          <button
            className="text-button"
            onClick={() => navigate("/releases/current-use")}
          >
            Release controls <ArrowRight size={14} />
          </button>
        </div>
      </div>
      <div className="metric-grid">
        {[
          {
            label: RETURN_COPY.active,
            value: w.cases?.length || 0,
            caption: "In your workspace",
            icon: FolderKanban,
            link: "/workspace/cases",
          },
          {
            label: "Tasks to focus on",
            value: open.length,
            caption: "Waiting for your next step",
            icon: ListChecks,
            link: "/productivity/tasks",
          },
          {
            label: "Defined tax rules",
            value: c.rules?.length || 0,
            caption: "From the frozen rule catalog",
            icon: Network,
            link: "/rules/explorer",
          },
          {
            label: "Certification gates",
            value: c.gates?.length || 0,
            caption: "Definitions · not executed",
            icon: ShieldCheck,
            link: "/certification/coverage",
          },
        ].map((m) => (
          <button
            className="metric-card"
            key={m.label}
            onClick={() => navigate(m.link)}
          >
            <div>
              <span>{m.label}</span>
              <m.icon size={17} />
            </div>
            <strong>{m.value.toLocaleString()}</strong>
            <p>
              {m.caption}
              <ArrowUpRight size={13} />
            </p>
          </button>
        ))}
      </div>
      <div className="dashboard-grid">
        <section className="card cases-panel">
          <div className="card-heading">
            <div>
              <h2>
                {RETURN_COPY.your}{" "}
                <span className="count-pill">{w.cases?.length || 0}</span>
              </h2>
              <p>The work that matters, all in one place.</p>
            </div>
            <button
              className="text-button"
              onClick={() => navigate("/workspace/cases")}
            >
              View all <ArrowRight size={14} />
            </button>
          </div>
          {w.cases?.length ? (
            <CaseTable compact />
          ) : (
            <div className="first-case">
              <div className="paper-stack">
                <div />
                <div />
                <div>
                  <FileText size={26} />
                  <span />
                  <span />
                  <span />
                </div>
              </div>
              <h3>A fresh start for precise work.</h3>
              <p>
                Create your first synthetic return. Facts, evidence, and every
                next step will stay connected.
              </p>
              <Button onClick={() => navigate("/intake/new")}>
                <Plus size={15} />
                {RETURN_COPY.first}
              </Button>
            </div>
          )}
        </section>
        <section className="card focus-panel">
          <div className="card-heading">
            <div>
              <h2>Your next move</h2>
              <p>A little structure. A lot of clarity.</p>
            </div>
            <Sparkles size={17} />
          </div>
          <div className="focus-steps">
            {[
              {
                n: "01",
                title: "Start with the source",
                text: "Create a return and capture the original facts.",
                path: "/intake/new",
                icon: Files,
              },
              {
                n: "02",
                title: "Connect the evidence",
                text: "Keep each document tied to its provenance.",
                path: "/intake/evidence-upload",
                icon: Network,
              },
              {
                n: "03",
                title: "Make every decision clear",
                text: "Review requirements before progressing.",
                path: "/review/preparer",
                icon: UserCheck,
              },
            ].map((t) => (
              <button key={t.n} onClick={() => navigate(t.path)}>
                <span className="focus-number">{t.n}</span>
                <div>
                  <strong>{t.title}</strong>
                  <p>{t.text}</p>
                </div>
                <ArrowUpRight size={15} />
              </button>
            ))}
          </div>
          <div className="focus-note">
            <ShieldCheck size={16} />
            <p>
              Source definitions are available. Runtime certification requires
              connected, qualified services.
            </p>
          </div>
        </section>
        <section className="card engine-panel">
          <div className="card-heading">
            <div>
              <h2>Designed to stay connected</h2>
              <p>From the first fact to the final explanation.</p>
            </div>
            <button
              className="text-button"
              onClick={() => navigate("/rules/dependencies")}
            >
              Explore graph <ArrowUpRight size={14} />
            </button>
          </div>
          <div className="pipeline">
            {[
              {
                name: "Intake",
                icon: ClipboardList,
                number: c.facts?.length || 0,
                label: "fact definitions",
              },
              {
                name: "Rule graph",
                icon: Network,
                number: c.rules?.length || 0,
                label: "primary rules",
              },
              {
                name: "Form 1040",
                icon: FileText,
                number: c.fields?.length || 0,
                label: "logical fields",
              },
              {
                name: "Validation",
                icon: ShieldCheck,
                number: c.tests?.length || 0,
                label: "test definitions",
              },
            ].map((p, i) => (
              <div key={p.name} className="pipeline-node">
                <div className="pipeline-icon">
                  <p.icon size={21} />
                </div>
                <h3>{p.name}</h3>
                <strong>{p.number.toLocaleString()}</strong>
                <span>{p.label}</span>
                {i < 3 && (
                  <div className="pipeline-connector">
                    <span />
                    <ChevronRight size={13} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="engine-foot">
            <span className="status-dot muted" />
            Catalog visibility is not execution or certification evidence.
          </div>
        </section>
        <section className="card activity-panel">
          <div className="card-heading">
            <div>
              <h2>Recent activity</h2>
              <p>A trace of your workspace.</p>
            </div>
            <button
              className="icon-btn"
              aria-label="Open activity"
              onClick={() => navigate("/workspace/activity")}
            >
              <ArrowUpRight size={17} />
            </button>
          </div>
          <ActivityList
            records={(w.activity || []).slice(0, 4)}
            empty="Your story starts here"
          />
        </section>
      </div>
    </>
  );
}

function ActivityList({ records, empty }: { records: Row[]; empty: string }) {
  const { workspace, returnDisplayIds } = useWorkspace();
  return records.length ? (
    <div className="activity-list">
      {records.map((r, i) => (
        <div key={r.id || i} className="activity-item">
          <div className="activity-dot">
            <Activity size={13} />
          </div>
          <div>
            <strong>
              {r.title
                ? taxText(r.title)
                : activityLabel(
                    r.action || r.type || r.eventType || "Workspace event",
                  )}
            </strong>
            <p>
              {r.message || r.summary || r.body || r.description
                ? returnSystemText(
                    r.message || r.summary || r.body || r.description,
                    workspace.cases || [],
                    returnDisplayIds,
                  )
                : r.actorName ||
                  taxText(r.entityType) ||
                  "Recorded in the workspace audit history"}
            </p>
            <small>{dateLabel(r.createdAt || r.timestamp)}</small>
          </div>
        </div>
      ))}
    </div>
  ) : (
    <Empty
      title={empty}
      description="Recorded activity will appear here as you work."
      icon={<History size={23} />}
    />
  );
}
function CaseSelector() {
  const {
    workspace,
    selected,
    selectCase,
    navigate,
    returnDisplayIds,
    technical,
  } = useWorkspace();
  return (
    <div className="case-context">
      <span className="mini-icon">
        <FolderKanban size={18} />
      </span>
      <div>
        <small>ACTIVE RETURN</small>
        {workspace.cases?.length ? (
          <select
            aria-label={RETURN_COPY.activeSelection}
            value={selected?.id || ""}
            onChange={(e) => selectCase(e.target.value)}
          >
            {workspace.cases.map((c: Row) => (
              <option key={c.id} value={c.id}>
                {displayReturnId(c, returnDisplayIds)} · {c.name}
              </option>
            ))}
          </select>
        ) : (
          <strong>{RETURN_COPY.noSelection}</strong>
        )}
      </div>
      {selected ? (
        <>
          <Badge>{human(selected.status)}</Badge>
          <span className="case-context-year">
            Tax year {selected.taxYear}
            {technical ? ` · Revision ${selected.revision}` : ""}
          </span>
        </>
      ) : (
        <Button onClick={() => navigate("/intake/new")}>
          <Plus size={14} />
          {RETURN_COPY.create}
        </Button>
      )}
    </div>
  );
}
function CaseTable({ compact = false }: { compact?: boolean }) {
  const { workspace, navigate, selectCase, returnDisplayIds } = useWorkspace();
  return (
    <DataTable
      records={workspace.cases || []}
      title={RETURN_COPY.plural}
      getSearchText={(record) => displayReturnId(record, returnDisplayIds)}
      columns={[
        {
          key: "name",
          label: RETURN_COPY.singular,
          render: (_, r) => (
            <div className="record-cell">
              <span className="record-avatar">
                {String(r.name).slice(0, 1)}
              </span>
              <span>
                <strong>{String(r.name)}</strong>
                <small>{displayReturnId(r, returnDisplayIds)}</small>
              </span>
            </div>
          ),
        },
        {
          key: "status",
          label: RETURN_COPY.status,
          render: (v) => <Badge>{human(v)}</Badge>,
        },
        { key: "taxYear", label: "Tax year" },
        ...(!compact
          ? [
              {
                key: "updatedAt",
                label: "Updated",
                render: (v: unknown) => dateLabel(v),
              },
            ]
          : []),
      ]}
      onInspect={(r) => {
        if (!selectCase(String(r.id))) return;
        navigate("/intake/questionnaire");
      }}
      emptyMessage={RETURN_COPY.empty}
    />
  );
}

function CreateCase() {
  const { refresh, navigate, selectCase, notify } = useWorkspace(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [returnName, setReturnName] = useState(""),
    [syntheticConsent, setSyntheticConsent] = useState(false);
  const validation = useFormValidation(
    "Create Return",
    createReturnValidationFields(returnName, syntheticConsent),
  );
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy || !validation.validate()) return;
    setBusy(true);
    setError("");
    const name = new FormData(e.currentTarget).get("name");
    try {
      const c = await post("/cases", { name, taxYear: 2025 });
      await refresh();
      selectCase(c.id);
      notify("Synthetic return created.");
      navigate("/intake/facts");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="split-layout">
      <section className="card form-card">
        <div className="form-card-icon">
          <FolderKanban size={25} />
        </div>
        <h2>A clear beginning.</h2>
        <p>
          Create a workspace for the facts, documents, and decisions behind this
          return.
        </p>
        <form onSubmit={submit} noValidate>
          <ValidationSummary issues={validation.issues} />
          <label>
            {RETURN_COPY.name}
            <input
              {...validation.fieldProps("create-return-name")}
              name="name"
              value={returnName}
              onChange={(event) => setReturnName(event.target.value)}
              placeholder="e.g. Synthetic W-2 reference return"
              required
              minLength={2}
              maxLength={120}
            />
            <FieldError issue={validation.issue("create-return-name")} />
          </label>
          <div className="form-two">
            <label>
              Tax year
              <input value="2025" readOnly />
            </label>
            <label>
              Return type
              <input value="Federal individual · W-2 income" readOnly />
            </label>
          </div>
          <label className="checkbox-label">
            <input
              {...validation.fieldProps("create-return-synthetic-consent")}
              type="checkbox"
              required
              checked={syntheticConsent}
              onChange={(event) => setSyntheticConsent(event.target.checked)}
            />
            I will use synthetic data only. This return cannot be submitted.
          </label>
          <FieldError
            issue={validation.issue("create-return-synthetic-consent")}
          />
          {error && (
            <div className="inline-error" role="alert">
              Create Return request could not be completed: {taxText(error)}
            </div>
          )}
          <div className="form-actions">
            <Button onClick={() => navigate("/workspace/cases")}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={busy}>
              {busy ? "Creating…" : RETURN_COPY.create}
              <ArrowRight size={15} />
            </Button>
          </div>
        </form>
      </section>
      <aside className="guidance-panel">
        <p className="eyebrow">A CONNECTED WORKSPACE</p>
        <h2>
          Start with facts.
          <br />
          Keep the full story.
        </h2>
        {[
          "Questions about the taxpayer and their tax situation",
          "Saved answers you can return to and update",
          "Original documents kept with the return",
          "Clear preparation and review steps",
        ].map((t) => (
          <div key={t}>
            <Check size={16} />
            <span>{t}</span>
          </div>
        ))}
        <Callout title="Draft, not a prepared return">
          Creating a return does not calculate tax, establish eligibility, or
          certify a return.
        </Callout>
      </aside>
    </div>
  );
}

function Facts({
  kind,
  readOnly = false,
}: {
  kind: string;
  readOnly?: boolean;
}) {
  const { catalog, selected, refresh, notify, inspect, session } =
      useWorkspace(),
    [editing, setEditing] = useState<Row | null>(null),
    [value, setValue] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const questionnaire = kind === "questionnaire";
  const list: Row[] =
    catalog[
      questionnaire
        ? "questions"
        : kind === "supplemental-facts"
          ? "supplementalFacts"
          : "facts"
    ] || [];
  const container = questionnaire ? "answers" : "facts";
  const validation = useFormValidation(
    "Technical fact capture",
    editing
      ? [
          {
            id: "legacy-fact-value",
            label: `${taxText(editing.name)} — Draft value`,
            value,
            required: true,
            validate: (input) => {
              try {
                parseDraftValue(editing, String(input ?? ""));
                return undefined;
              } catch (reason) {
                return (reason as Error).message;
              }
            },
          },
        ]
      : [],
  );
  const rows: Row[] = list.map((r: Row): Row => ({
    ...r,
    captured:
      selected?.[container]?.[r.id] === undefined
        ? "Not captured"
        : String(
            typeof selected[container][r.id] === "object"
              ? JSON.stringify(selected[container][r.id])
              : selected[container][r.id],
          ),
    captureState:
      selected?.[container]?.[r.id] === undefined
        ? "Missing"
        : "Draft captured",
  }));
  const close = useCallback(() => {
    setEditing(null);
    setError("");
  }, []);
  const editable = (r: Row) =>
    !readOnly && canCapture(r, kind, session.user.role);
  async function save() {
    if (!selected || !editing || !editable(editing)) return;
    if (!validation.validate()) return;
    setBusy(true);
    setError("");
    try {
      const val = parseDraftValue(editing, value);
      await patch("/cases/" + selected.id, {
        expectedRevision: selected.revision,
        [container]: { [editing.id]: val },
      });
      await refresh();
      close();
      notify("Draft fact saved. Not yet validated or admitted.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <CaseSelector />
      {readOnly && (
        <Callout title="Runtime values are not available">
          The draft facts below are read-only here. Only a verified evaluator
          run may produce runtime facts and results.
        </Callout>
      )}
      <div className="inline-stats">
        <span>
          <strong>
            {rows.filter((r) => r.captureState === "Draft captured").length}
          </strong>{" "}
          captured
        </span>
        <span>
          <strong>{rows.length}</strong> source definitions
        </span>
        <span>Draft capture · no eligibility claim</span>
      </div>
      <DataTable
        records={rows}
        columns={[
          {
            key: "id",
            label: "Fact identifier",
            render: (v) => <code>{String(v)}</code>,
          },
          {
            key: "name",
            label: questionnaire ? "Question" : "Fact",
            render: (v) => taxText(v),
          },
          {
            key: "captureState",
            label: "Capture state",
            render: (v) => <Badge>{String(v)}</Badge>,
          },
          { key: "captured", label: "Draft value" },
          {
            key: "action",
            label: "",
            sortable: false,
            render: (_, r) => (
              <Button
                disabled={!selected || !editable(r)}
                onClick={() => {
                  setEditing(r);
                  setValue(serializeDraftValue(selected?.[container]?.[r.id]));
                }}
              >
                {editable(r) ? "Capture" : "Read only"}
              </Button>
            ),
          },
        ]}
        onInspect={inspect}
      />
      {editing && (
        <Modal title={taxText(editing.name)} close={close}>
          <div className="detail-body">
            <ValidationSummary issues={validation.issues} />
            <code className="field-id">{editing.id}</code>
            <p>
              {taxText(
                editing.label ||
                  editing.description ||
                  "Capture the original fact without assuming an answer.",
              )}
            </p>
            <label>
              Draft value
              {structuredCapture(editing) ? (
                <textarea
                  {...validation.fieldProps("legacy-fact-value")}
                  rows={7}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Enter structured JSON matching the original field contract"
                  spellCheck={false}
                />
              ) : Array.isArray(editing.options) &&
                editing.options.length > 0 ? (
                <select
                  {...validation.fieldProps("legacy-fact-value")}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                >
                  <option value="">Choose an explicit answer</option>
                  {editing.options.map((o: any, i: number) => {
                    const v =
                      typeof o === "object" ? (o.value ?? o.id ?? o.code) : o;
                    return (
                      <option key={i} value={String(v)}>
                        {String(
                          typeof o === "object" ? (o.label ?? o.name ?? v) : o,
                        )}
                      </option>
                    );
                  })}
                </select>
              ) : /bool/i.test(String(editing.type)) ? (
                <select
                  {...validation.fieldProps("legacy-fact-value")}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                >
                  <option value="">Not answered</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : (
                <input
                  {...validation.fieldProps("legacy-fact-value")}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Enter original source value"
                />
              )}
              <FieldError issue={validation.issue("legacy-fact-value")} />
            </label>
            {structuredCapture(editing) && (
              <details>
                <summary>Original value contract</summary>
                <pre className="json-view">
                  {JSON.stringify(
                    editing.valueSchema || editing.answerType,
                    null,
                    2,
                  )}
                </pre>
              </details>
            )}
            <Callout title="Unvalidated draft">
              The original type, provenance, evidence, and admission contracts
              must be independently validated before promotion. Saving does not
              establish a verified fact.
            </Callout>
            {error && (
              <p className="inline-error" role="alert">
                Technical fact capture request could not be completed:{" "}
                {taxText(error)}
              </p>
            )}
            <div className="form-actions">
              <Button onClick={close}>Cancel</Button>
              <Button variant="primary" disabled={busy} onClick={save}>
                {busy ? "Saving…" : "Save draft"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function Evidence({
  upload = false,
  w2 = false,
  embedded = false,
}: {
  upload?: boolean;
  w2?: boolean;
  embedded?: boolean;
}) {
  const { selected, refresh, inspect, notify, catalog, session, technical, workspace } =
      useWorkspace(),
    [loadedDocuments, setLoadedDocuments] = useState<{
      returnId: string;
      records: Row[];
    }>({ returnId: "", records: [] }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [drag, setDrag] = useState(false),
    [chosenFiles, setChosenFiles] = useState<File[]>([]);
  const canUpload = ["workspace_admin", "preparer"].includes(session.user.role);
  const uploadInFlight = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const validationSection = w2 ? "W-2 documents" : "Supporting documents";
  const validation = useFormValidation(
    validationSection,
    evidenceValidationFields(chosenFiles),
  );
  const selectedCaseRef = useRef(selected?.id);
  selectedCaseRef.current = selected?.id;
  const records =
    loadedDocuments.returnId === selected?.id ? loadedDocuments.records : [];
  const load = useCallback(async () => {
    if (selected) {
      const data = await api("/cases/" + selected.id + "/evidence");
      if (selectedCaseRef.current === selected.id)
        setLoadedDocuments({
          returnId: selected.id,
          records: Array.isArray(data) ? data : data.evidence || [],
        });
    } else setLoadedDocuments({ returnId: "", records: [] });
  }, [selected?.id]);
  useEffect(() => {
    setError("");
    setChosenFiles([]);
    load().catch((e) => {
      if (selectedCaseRef.current === selected?.id)
        setError(
          technical
            ? e.message
            : preparationError(e, {
                sectionLabel: validationSection,
                fieldLabel: "Documents",
              }),
        );
    });
  }, [load]);
  async function addFiles(files: FileList | File[]) {
    if (!selected || !canUpload || uploadInFlight.current) return;
    const fileArray = Array.from(files);
    if (!fileArray.length) return;
    setChosenFiles(fileArray);
    setError("");
    const fileIssues = validateFormFields(
      validationSection,
      evidenceValidationFields(fileArray),
    );
    if (fileIssues.length) {
      focusValidationIssue(fileIssues[0]);
      return;
    }
    uploadInFlight.current = true;
    setBusy(true);
    setError("");
    let saved = 0;
    try {
      for (const f of fileArray) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1]);
          reader.onerror = () =>
            reject(new Error("The selected document could not be read."));
          reader.readAsDataURL(f);
        });
        await post("/cases/" + selected.id + "/evidence", {
          name: f.name,
          mimeType: f.type || "text/plain",
          base64,
        });
        saved++;
      }
      await load();
      await refresh();
      if (selectedCaseRef.current === selected.id) setChosenFiles([]);
      notify("Documents added to the return. They still need review.");
    } catch (e) {
      setError(
        `${saved ? `${saved} document(s) stored before this interruption. ` : ""}${e instanceof ApiError && !technical ? preparationError(e, { sectionLabel: validationSection, fieldLabel: "Documents" }) : (e as Error).message}`,
      );
      if (saved) {
        try {
          await load();
          await refresh();
        } catch {
          /* Retain the original failure; never report the batch as complete. */
        }
      }
    } finally {
      uploadInFlight.current = false;
      setBusy(false);
    }
  }
  const duplicateCount = records.filter(
    (r, i) => records.findIndex((x) => x.sha256 === r.sha256) < i,
  ).length;
  return (
    <>
      {!embedded && <CaseSelector />}
      {!canUpload && (
        <Callout title="Read-only evidence access">
          Your role can inspect retained documents but cannot upload or replace
          evidence.
        </Callout>
      )}
      {(upload || !records.length) && (
        <>
          <ValidationSummary issues={validation.issues} />
          <div
            className={
              "upload-zone" +
              (drag ? " dragging" : "") +
              (!selected || !canUpload ? " disabled" : "")
            }
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              if (selected && canUpload) void addFiles(e.dataTransfer.files);
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              aria-label="Select documents to upload"
              accept="application/pdf,image/png,image/jpeg,text/plain"
              multiple
              disabled={!selected || !canUpload || busy}
              onChange={(e) => {
                if (e.target.files) void addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <span className="upload-icon">
              <UploadCloud size={28} />
            </span>
            <h2>
              {busy
                ? "Adding documents…"
                : w2
                  ? "Add W-2 forms"
                  : "Add supporting documents"}
            </h2>
            <p>Drop synthetic documents here, or choose files below.</p>
            <button
              type="button"
              className="btn"
              {...validation.fieldProps("evidence-documents")}
              data-invalid={Boolean(validation.issue("evidence-documents"))}
              disabled={!selected || !canUpload || busy}
              onClick={() => fileInputRef.current?.click()}
            >
              Browse documents
            </button>
            <FieldError issue={validation.issue("evidence-documents")} />
            <span>
              PDF, PNG, JPG, TXT · Up to 5 MB each · Documents retained as
              uploaded
            </span>
          </div>
        </>
      )}
      {error && (
        <p role="alert" className="inline-error">
          {validationSection} request could not be completed: {taxText(error)}
        </p>
      )}
      <div className="inline-stats">
        <span>
          <strong>{records.length}</strong> documents
        </span>
        <span>
          <strong>{duplicateCount}</strong> possible duplicate documents
        </span>
        <span>Uploads are not verified tax evidence</span>
      </div>
      <DataTable
        records={records}
        columns={[
          {
            key: "name",
            label: "Document",
            render: (v) => (
              <span className="file-cell">
                <FileText size={17} />
                {String(v)}
              </span>
            ),
          },
          { key: "mimeType", label: "Type" },
          {
            key: "size",
            label: "Size",
            render: (v) => `${Math.ceil(Number(v) / 1024)} KB`,
          },
          ...(technical
            ? [
                {
                  key: "sha256",
                  label: "Fingerprint",
                  render: (v: unknown) => (
                    <code title={String(v)}>{String(v).slice(0, 16)}…</code>
                  ),
                },
              ]
            : []),
          { key: "createdAt", label: "Added", render: dateLabel },
          {
            key: "action",
            label: "",
            render: (_, r) => (
              <a
                className="btn"
                href={
                  "/api/cases/" +
                  selected?.id +
                  "/evidence/" +
                  r.id +
                  "/content"
                }
                target="_blank"
                rel="noreferrer"
              >
                Open <ArrowUpRight size={13} />
              </a>
            ),
          },
        ]}
        onInspect={inspect}
      />
      {selected && ["workspace_admin","preparer","reviewer"].includes(session.user.role) && workspace.capabilities?.w2Extraction?.enabled && (
        <W2ExtractionReview key={selected.id} returnId={selected.id} revision={selected.revision} canReview={canUpload} documents={records} onSaved={refresh} />
      )}
      {w2 && !technical && (
        <Callout title="Include every wage statement">
          Add a W-2 from each employer, along with any corrected forms and
          related statements. Uploading a form does not copy its amounts into
          the return or resolve differences automatically.
        </Callout>
      )}
      {w2 && technical && (
        <div className="card spaced">
          <div className="card-heading">
            <h2>Complete TY2025 W-2 source field inventory</h2>
            <Badge>{catalog.w2Fields?.length || 0} defined fields</Badge>
          </div>
          <CatalogTable records={catalog.w2Fields || []} />
        </div>
      )}
    </>
  );
}

function CatalogTable({ records, title }: { records: Row[]; title?: string }) {
  const { inspect, workspace, notify, refresh } = useWorkspace();
  return (
    <DataTable
      records={records}
      title={title}
      getRowId={idOf}
      getSearchText={(record) => taxText(record.name)}
      columns={[
        {
          key: "id",
          label: "Identifier",
          render: (v) => <code>{String(v)}</code>,
        },
        { key: "name", label: "Definition", render: (v) => taxText(v) },
        { key: "category", label: "Family", render: (v) => taxText(v) },
        {
          key: "status",
          label: "Source status",
          render: (v) => <Badge>{human(v || "Defined")}</Badge>,
        },
      ]}
      onInspect={inspect}
      pinnedIds={(workspace.pins || [])
        .filter(
          (p: Row) =>
            p.targetType === "catalog" &&
            records.some((r) => idOf(r) === p.targetId),
        )
        .map((p: Row) => p.targetId)}
      savedViews={(workspace.views || [])
        .filter((v: Row) => v.filters?.screen === location.pathname)
        .map((v: Row) => ({ ...v.filters, id: v.id, name: v.name }))}
      onSaveView={async (view) => {
        try {
          await post("/views", {
            name: view.name,
            filters: { ...view, screen: location.pathname },
          });
          await refresh();
          notify("Saved view stored.");
        } catch (e) {
          notify((e as Error).message);
          throw e;
        }
      }}
      onPinChange={async (ids) => {
        try {
          const known = new Set(records.map(idOf)),
            current = (workspace.pins || []).filter(
              (p: Row) => p.targetType === "catalog" && known.has(p.targetId),
            );
          for (const id of ids)
            if (!current.some((p: Row) => p.targetId === id))
              await post("/pins", {
                targetType: "catalog",
                targetId: id,
                label: String(
                  records.find((r) => idOf(r) === id)?.name || id,
                ).slice(0, 120),
              });
          for (const p of current)
            if (!ids.includes(p.targetId)) await remove("/pins/" + p.id);
          await refresh();
        } catch (e) {
          notify((e as Error).message);
          await refresh();
          throw e;
        }
      }}
      bulkActions={[
        {
          label: "Export selection",
          onAction: (rows) => downloadJson("zentrotax-selection.json", rows),
        },
      ]}
    />
  );
}

function Tasks({ notes = false }: { notes?: boolean }) {
  const { workspace, selected, session, refresh, notify, technical } =
      useWorkspace(),
    [title, setTitle] = useState(""),
    [body, setBody] = useState(""),
    [items, setItems] = useState<Row[]>([]),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const canWrite = (
    notes
      ? ["workspace_admin", "preparer", "reviewer"]
      : ["workspace_admin", "preparer"]
  ).includes(session.user.role);
  const validationSection = notes ? "Review notes" : "Workspace tasks";
  const validation = useFormValidation(
    validationSection,
    canWrite && (!notes || selected)
      ? taskValidationFields(notes, title, body)
      : [],
  );
  const currentNotesReturn = useRef(selected?.id);
  currentNotesReturn.current = selected?.id;
  useEffect(() => {
    if (!notes) return;
    window.dispatchEvent(
      new CustomEvent("zt:intake-dirty", {
        detail: { dirty: Boolean(title || body) },
      }),
    );
    return () => {
      window.dispatchEvent(
        new CustomEvent("zt:intake-dirty", { detail: { dirty: false } }),
      );
    };
  }, [notes, title, body]);
  const load = useCallback(async () => {
    if (notes) {
      if (!selected) {
        setItems([]);
        return;
      }
      const a = await api(
        "/notes" + (selected ? "?caseId=" + selected.id : ""),
      );
      if (currentNotesReturn.current === selected.id)
        setItems(
          notesForReturn(Array.isArray(a) ? a : a.notes || [], selected.id),
        );
    }
  }, [notes, selected?.id]);
  useEffect(() => {
    load().catch((e) =>
      setError(
        technical
          ? e.message
          : preparationError(e, { sectionLabel: validationSection }),
      ),
    );
  }, [load]);
  const records = notes
    ? notesForReturn(items, selected?.id).map((r) => ({
        ...r,
        title: String(r.body).split("\n")[0],
      }))
    : workspace.tasks || [];
  async function save(e: FormEvent) {
    e.preventDefault();
    if (!canWrite || busy || (notes && !selected)) return;
    if (!validation.validate()) return;
    setBusy(true);
    setError("");
    try {
      await post(
        notes ? "/notes" : "/tasks",
        notes
          ? { body: title + "\n" + body, caseId: selected?.id || null }
          : { title, caseId: selected?.id || null },
      );
      setTitle("");
      setBody("");
      await refresh();
      await load();
      notify(
        notes ? "Review note recorded. No approval issued." : "Task created.",
      );
    } catch (e) {
      setError(
        technical
          ? (e as Error).message
          : preparationError(e, {
              sectionLabel: validationSection,
              fieldLabel: notes ? "Note title and details" : "Task title",
            }),
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      {notes && <CaseSelector />}
      <div className="split-layout">
        <section className="card">
          <div className="card-heading">
            <h2>{notes ? "Working review notes" : "Workspace tasks"}</h2>
            <Badge>{records.length} records</Badge>
          </div>
          <DataTable
            records={records}
            columns={[
              { key: "title", label: notes ? "Note" : "Task" },
              ...(notes ? [{ key: "body", label: "Details" }] : []),
              {
                key: "status",
                label: "Status",
                render: (v) => <Badge>{human(v || "Recorded")}</Badge>,
              },
              { key: "createdAt", label: "Created", render: dateLabel },
              ...(!notes
                ? [
                    {
                      key: "action",
                      label: "",
                      render: (_: unknown, r: Row) => (
                        <Button
                          disabled={!canWrite || busy}
                          onClick={async () => {
                            try {
                              await patch("/tasks/" + r.id, {
                                status: r.status === "done" ? "todo" : "done",
                              });
                              await refresh();
                            } catch (e) {
                              notify(
                                technical
                                  ? (e as Error).message
                                  : preparationError(e, {
                                      sectionLabel: "Workspace tasks",
                                      fieldLabel: "Task status",
                                    }),
                              );
                            }
                          }}
                        >
                          {r.status === "done" ? "Reopen" : "Complete"}
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]}
            emptyMessage={
              notes
                ? "No local review notes. Record observations without implying approval."
                : "Your task list is clear."
            }
          />
        </section>
        <section className="card form-card">
          <h2>{notes ? "Record an observation" : "Create a task"}</h2>
          <p>
            {notes
              ? "A working note is not a signed review decision."
              : "Keep the next step specific and actionable."}
          </p>
          {!canWrite && (
            <Callout title="Read-only access">
              Your workspace role can read these records, but cannot add or
              change them.
            </Callout>
          )}
          <form onSubmit={save} noValidate>
            <ValidationSummary issues={validation.issues} />
            {notes && !selected && (
              <p>Select a return before adding a review note.</p>
            )}
            <label>
              {notes ? "Note title" : "Task title"}
              <input
                {...validation.fieldProps(
                  notes ? "review-note-title" : "task-title",
                )}
                disabled={!canWrite || busy || (notes && !selected)}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                maxLength={180}
              />
              <FieldError
                issue={validation.issue(
                  notes ? "review-note-title" : "task-title",
                )}
              />
            </label>
            {notes && (
              <label>
                Details
                <textarea
                  {...validation.fieldProps("review-note-details")}
                  disabled={!canWrite || busy || !selected}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  maxLength={8000}
                />
                <FieldError issue={validation.issue("review-note-details")} />
              </label>
            )}
            {error && (
              <p className="inline-error" role="alert">
                {validationSection} request could not be completed:{" "}
                {taxText(error)}
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={!canWrite || busy || (notes && !selected)}
            >
              <Plus size={15} />
              {busy ? "Saving…" : notes ? "Record note" : "Add task"}
            </Button>
          </form>
        </section>
      </div>
    </>
  );
}

function Security({ kind }: { kind: string }) {
  const { session, refresh, notify, inspect } = useWorkspace(),
    [sessions, setSessions] = useState<Row[]>([]),
    [enroll, setEnroll] = useState<MfaEnrollment | null>(null),
    [recovery, setRecovery] = useState<string[]>([]),
    [code, setCode] = useState(""),
    [password, setPassword] = useState(""),
    [error, setError] = useState(""),
    [codeError, setCodeError] = useState(""),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const mfaEnabled = Boolean(session.user.mfaEnabled || confirmed);
  const canEnroll = !mfaEnabled && !enroll && kind !== "password-reset";
  const validation = useInteractiveFormValidation(
    enroll ? "Authenticator verification" : "Authenticator setup",
    securityValidationFields(
      Boolean(enroll),
      Boolean(canEnroll),
      password,
      code,
    ).map((field) =>
      field.id === "security-authenticator-code"
        ? { ...field, validate: () => codeError || undefined }
        : field,
    ),
    `${session.user.id}:${enroll ? "verification" : "setup"}`,
  );
  const load = useCallback(async () => {
    const r = await api("/auth/sessions");
    setSessions(Array.isArray(r) ? r : r.sessions || []);
  }, []);
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [load]);
  function expireEnrollment() {
    setEnroll(null);
    setCode("");
    setPassword("");
    setCodeError("");
    setError(mfaSetupError("MFA_ENROLLMENT_EXPIRED", session.user.email));
  }
  useEffect(() => {
    if (!enroll) return;
    const timer = window.setTimeout(
      expireEnrollment,
      Math.max(0, enroll.expiresAt - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [enroll]);
  async function action(name: "enroll" | "confirm") {
    if (pending.current) return;
    if (name === "confirm" && (!enroll || enroll.expiresAt <= Date.now())) {
      expireEnrollment();
      return;
    }
    if (!validation.validate()) return;
    pending.current = true;
    setBusy(true);
    try {
      setError("");
      setCodeError("");
      if (name === "enroll") {
        const started = Date.now();
        const next = readMfaEnrollment(
          await post("/auth/mfa/enrollment", { password }),
          session.user.email,
          started,
        );
        setPassword("");
        setCode("");
        if (next.expiresAt <= Date.now()) expireEnrollment();
        else setEnroll(next);
      } else {
        const result = await post("/auth/mfa/confirm", { code });
        setError("");
        setCodeError("");
        setConfirmed(true);
        setRecovery(result.recoveryCodes || []);
        setEnroll(null);
        setCode("");
        setPassword("");
        // A successful confirmation must not be retried if session refresh fails.
        try {
          await refresh();
          notify("Authenticator verified and enabled.");
        } catch {
          setError(
            "Your authenticator was enabled, but account status could not refresh. Store your recovery codes, then reload this page. Do not verify again.",
          );
        }
      }
    } catch (e) {
      const failure = e instanceof ApiError ? e.code : "REQUEST_FAILED";
      if (failure === "MFA_ENROLLMENT_EXPIRED") expireEnrollment();
      else if (failure === "INVALID_MFA_CODE" && name === "confirm") {
        if (enroll && enroll.expiresAt <= Date.now()) expireEnrollment();
        else setCodeError(mfaSetupError(failure, session.user.email));
      } else {
        if (failure === "MFA_ALREADY_ENABLED") {
          setConfirmed(true);
          setEnroll(null);
          setCode("");
          setPassword("");
        }
        setError(mfaSetupError(failure, session.user.email));
      }
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  if (recovery.length)
    return (
      <section className="card form-card">
        <div className="form-card-icon">
          <ShieldCheck size={25} />
        </div>
        <h2>Keep your recovery codes safe.</h2>
        <p>
          These one-time codes are shown only now. Store them somewhere secure
          before leaving this screen.
        </p>
        <div className="recovery-codes">
          {recovery.map((code) => (
            <code key={code}>{code}</code>
          ))}
        </div>
        <Button variant="primary" onClick={() => setRecovery([])}>
          I have stored my recovery codes
        </Button>
        {error && (
          <p className="inline-error" role="alert">
            {error}
          </p>
        )}
      </section>
    );
  return (
    <div className="security-layout">
      <section className="card form-card">
        <div className="form-card-icon">
          <Fingerprint size={26} />
        </div>
        <h2>Your identity. Protected.</h2>
        <p>{session.user.email}</p>
        <KeyValue label="Workspace role">{human(session.user.role)}</KeyValue>
        <KeyValue label="Authenticator">
          {mfaEnabled ? "Enabled" : "Not enabled"}
        </KeyValue>
        {kind === "password-reset" ? (
          <Callout title="Delivery service required">
            Password recovery does not issue visible reset tokens. Connect a
            qualified email delivery service before enabling account recovery.
          </Callout>
        ) : (
          <>
            <ValidationSummary issues={validation.issues} />
            {canEnroll && (
              <>
                <p>
                  Set up a separate authenticator entry for{" "}
                  <strong>{session.user.email}</strong>. First verify this
                  account's password, then scan its QR code.
                </p>
                <p>
                  If you started setup earlier, starting again replaces that
                  pending setup. Use the new QR code, not an older entry.
                </p>
                <label>
                  This account's current password
                  <input
                    {...validation.fieldProps("security-current-password")}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError("");
                    }}
                    maxLength={256}
                    disabled={busy}
                  />
                  <FieldError
                    issue={validation.issue("security-current-password")}
                  />
                </label>
              </>
            )}
            {enroll && (
              <AuthenticatorEnrollment
                enrollment={enroll}
                email={session.user.email}
              />
            )}
            {enroll && (
              <label>
                6-digit code for {session.user.email}
                <input
                  {...validation.fieldProps("security-authenticator-code")}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setCodeError("");
                  }}
                  placeholder="6-digit code"
                  disabled={busy}
                />
                <FieldError
                  issue={validation.issue("security-authenticator-code")}
                />
              </label>
            )}
            <div className="form-actions">
              {canEnroll && (
                <Button onClick={() => action("enroll")} disabled={busy}>
                  Set up authenticator
                </Button>
              )}
              {enroll && (
                <Button
                  variant="primary"
                  onClick={() => action("confirm")}
                  disabled={busy}
                >
                  Verify and enable
                </Button>
              )}
            </div>
          </>
        )}
        {error && (
          <div className="inline-error" role="alert">
            Account security request could not be completed: {taxText(error)}
          </div>
        )}
      </section>
      <section className="card">
        <div className="card-heading">
          <div>
            <h2>Active sessions</h2>
            <p>Review and revoke access to this workspace.</p>
          </div>
          <LockKeyhole size={19} />
        </div>
        <DataTable
          records={sessions}
          columns={[
            {
              key: "device",
              label: "Device / browser",
              render: (v) => (
                <span className="truncate">{String(v || "Local session")}</span>
              ),
            },
            { key: "createdAt", label: "Signed in", render: dateLabel },
            { key: "expiresAt", label: "Expires", render: dateLabel },
            {
              key: "action",
              label: "",
              render: (_, r) =>
                r.current ? (
                  <Badge>Current session</Badge>
                ) : (
                  <Button
                    onClick={async () => {
                      try {
                        await remove("/auth/sessions/" + r.id);
                        await load();
                        notify("Session revoked.");
                      } catch (e) {
                        notify((e as Error).message);
                      }
                    }}
                  >
                    Revoke
                  </Button>
                ),
            },
          ]}
          onInspect={inspect}
        />
      </section>
    </div>
  );
}

function RuntimePanel({ screen }: { screen: Screen }) {
  const { catalog, inspect } = useWorkspace();
  const phaseMap: Record<string, string[]> = {
    compiler: ["2B_5"],
    evaluator: ["2B_6"],
    validation: ["2B_9"],
    review: ["2B_9", "2B_12"],
    forms: ["2B_8"],
    certification: ["2B_10", "2B_12"],
    releases: ["2B_11"],
    audit: ["2B_11", "2B_12"],
  };
  const needles = phaseMap[screen.module] || [];
  const operations = (catalog.operations || []).filter(
    (r: Row) =>
      !needles.length || needles.some((n) => r.source?.path?.includes(n + "_")),
  );
  return (
    <>
      <div className="runtime-hero">
        <div className="runtime-symbol">
          <Workflow size={30} />
        </div>
        <div>
          <p className="eyebrow">{human(screen.module)} CONTROL PLANE</p>
          <h2>
            Precise contracts.
            <br />
            Explicit execution boundaries.
          </h2>
          <p>
            Explore the original operations and their requirements. No job is
            queued or result created without a connected, qualified backend.
          </p>
        </div>
        <div className="runtime-status">
          <span className="status-dot muted" />
          <strong>Service not connected</strong>
          <small>Execution controls locked</small>
        </div>
      </div>
      <Callout title="Required service adapter unavailable">
        Local intake and workspace records are active. Compiler, evaluator,
        finalization, approval, signature, and certification operations require
        their original qualified services; this console does not simulate them.
      </Callout>
      <CatalogTable title="Integration operations" records={operations} />
    </>
  );
}

function DesignSystem() {
  return (
    <div className="design-grid">
      <section className="card form-card">
        <p className="eyebrow">01 · COLOR</p>
        <h2>A singular point of clarity.</h2>
        <div className="swatches">
          {["#04E143", "#063519", "#52715A", "#EAF7EE", "#FFFFFF"].map((v) => (
            <div key={v}>
              <span style={{ background: v }} />
              <code>{v}</code>
            </div>
          ))}
        </div>
        <p>
          One green. Deliberate tonal depth. Status always includes text, never
          color alone.
        </p>
      </section>
      <section className="card form-card">
        <p className="eyebrow">02 · TYPOGRAPHY</p>
        <h1>Precision, by design.</h1>
        <h2>A confident hierarchy.</h2>
        <p>Readable details. Quiet rhythm. Clear decisions.</p>
        <code>FED-IND-TY2025-W2-S1</code>
      </section>
      <section className="card form-card">
        <p className="eyebrow">03 · ACTIONS & STATES</p>
        <div className="button-samples">
          <Button variant="primary">
            <Plus size={15} />
            Primary action
          </Button>
          <Button>Secondary</Button>
          <Button disabled>
            <LockKeyhole size={15} />
            Unavailable
          </Button>
          <Badge>Draft captured</Badge>
        </div>
        <label>
          Accessible form control
          <input placeholder="Visible labels, clear focus" />
        </label>
        <Progress value={60} label="Design sample, not return progress" />
        <small>Illustrative component sample · not readiness evidence</small>
      </section>
      <section className="card form-card">
        <p className="eyebrow">04 · INTERACTION</p>
        <h2>Keyboard-first. Motion-aware.</h2>
        <KeyValue label="Global search">
          <kbd>Ctrl / ⌘ K</kbd>
        </KeyValue>
        <KeyValue label={RETURN_COPY.new}>
          <kbd>N</kbd>
        </KeyValue>
        <KeyValue label="Dismiss dialog">
          <kbd>Esc</kbd>
        </KeyValue>
        <p>
          Visible focus rings, semantic headings, dialog focus containment,
          reduced-motion and high-contrast support.
        </p>
      </section>
    </div>
  );
}

function ScreenView({ screen }: { screen: Screen }) {
  const {
      catalog: c,
      workspace: w,
      session,
      selected,
      selectCase,
      inspect,
      navigate,
      refresh,
      notify,
      returnDisplayIds,
      technical,
      technicalDetails,
    } = useWorkspace(),
    k = screen.kind;
  const canWrite = ["workspace_admin", "preparer"].includes(session.user.role);
  if (!technical && isTechnicalScreen(screen))
    return (
      <Empty
        title="This is a technical workspace"
        description="Taxpayer information, documents and preparation steps are available in Intake. System records are kept in the administrator’s Technical Details workspace."
        action={
          <Button
            variant="primary"
            onClick={() => navigate("/intake/questionnaire")}
          >
            Go to intake
          </Button>
        }
      />
    );
  if (
    technical &&
    ["/compiler/dashboard", "/evaluator/console"].includes(screen.path)
  )
    return (
      <EngineWorkbench
        isAdministrator={session.user.role === "workspace_admin"}
        sessionScope={`${session.user.id}:${session.workspace?.id || ""}`}
      />
    );
  if (k === "case-create" && !canWrite)
    return (
      <Callout title="Read-only workspace access">
        Your current role does not permit creating or changing intake drafts.
        Ask a workspace administrator for an appropriate assignment.
      </Callout>
    );
  if (
    screen.path === "/productivity/saved-views" ||
    screen.path === "/productivity/pinned" ||
    screen.path === "/settings/preferences"
  )
    return (
      <PersonalTools
        technical={technical}
        kind={
          screen.path.endsWith("saved-views")
            ? "saved-views"
            : screen.path.endsWith("pinned")
              ? "pinned"
              : "preferences"
        }
        views={
          technical
            ? w.views || []
            : (w.views || []).filter((v: Row) => {
                const destination = screens.find((s) =>
                  [s.path, s.id, s.kind].includes(v.filters?.screen),
                );
                return destination && !isTechnicalScreen(destination);
              })
        }
        pins={
          technical
            ? w.pins || []
            : (w.pins || []).filter((p: Row) => p.targetType === "case")
        }
        returnDisplayIds={returnDisplayIds}
        refresh={refresh}
        navigate={navigate}
        inspect={inspect}
        notify={notify}
      />
    );
  if (k === "case-create") return <CreateCase />;
  if (k === "assignments")
    return (
      <CaseAssignments
        cases={w.cases || []}
        returnDisplayIds={returnDisplayIds}
        user={session.user}
        onOpen={(record) => {
          if (!selectCase(String(record.id))) return;
          navigate("/intake/questionnaire");
        }}
        refresh={refresh}
        notify={notify}
      />
    );
  if (k === "cases")
    return (
      <section className="card">
        <CaseTable />
      </section>
    );
  if (!technical && topicPreparationPaths.has(screen.path))
    return (
      <>
        <CaseSelector />
        <ReturnBuilder
          key={`${selected?.id || "none"}:${screen.path}`}
          catalog={c}
          selected={selected}
          role={session.user.role}
          refresh={refresh}
          notify={notify}
          navigate={navigate}
          initialStage={initialBuilderStage(screen.path)}
          onTechnicalDetails={technicalDetails}
          renderDocuments={() => <Evidence upload embedded />}
          diagnosticsEnabled={w.capabilities?.intakeDiagnostics?.enabled === true}
        />
      </>
    );
  if (["questionnaire", "facts", "supplemental-facts"].includes(k))
    return screen.module === "intake" ? (
      <>
        <CaseSelector />
        <IntakeExperience
          key={`${selected?.id || "none"}:${k}`}
          kind={k}
          catalog={c}
          selected={selected}
          role={session.user.role}
          refresh={refresh}
          notify={notify}
          onTechnicalDetails={technicalDetails}
        />
      </>
    ) : (
      <Facts kind={k} readOnly={screen.module === "evaluator"} />
    );
  if (!technical && (k === "admission" || k === "reconciliation"))
    return (
      <>
        <CaseSelector />
        <IntakeReview
          questions={c.questions || []}
          choiceMasters={c.choiceMasters || []}
          selected={selected}
          navigate={navigate}
          reconciliation={k === "reconciliation"}
        />
      </>
    );
  if (!technical && k === "form")
    return (
      <>
        <CaseSelector />
        <FormPreparationStatus navigate={navigate} />
      </>
    );
  if (k === "upload" || k === "evidence")
    return <Evidence upload={k === "upload"} w2={screen.path.includes("w2")} />;
  if (k === "tasks") return <Tasks />;
  if (k === "findings" || k === "review")
    return (
      <>
        <Callout title="Working review workspace">
          Observations recorded here are local notes, not signed review
          decisions, independent approvals, or certification evidence.
        </Callout>
        <Tasks key={selected?.id || "no-return"} notes />
      </>
    );
  if (["mfa", "sessions", "devices", "login", "password-reset"].includes(k))
    return <Security kind={k} />;
  if (k === "design-system") return <DesignSystem />;
  if (k === "notifications")
    return (
      <NotificationCenter
        technical={technical}
        records={w.notifications || []}
        returnRecords={w.cases || []}
        returnDisplayIds={returnDisplayIds}
        refresh={refresh}
        notify={notify}
      />
    );
  if (k === "audit")
    return (
      <AuditEvents
        records={screen.path === "/audit/explorer" ? w.activity || [] : []}
        returnRecords={w.cases || []}
        returnDisplayIds={returnDisplayIds}
        inspect={inspect}
        title={screen.title}
      />
    );
  if (k === "activity")
    return (
      <section className="card">
        <div className="card-heading">
          <h2>Recorded workspace events</h2>
          <Badge>Actual local records</Badge>
        </div>
        <ActivityList
          records={
            screen.module === "evidence"
              ? (w.activity || []).filter((event: Row) =>
                  isDocumentActivity(event),
                )
              : (w.activity || []).filter(
                  (event: Row) =>
                    technical || event.type !== "engine_mechanics",
                )
          }
          empty="No recorded events"
        />
      </section>
    );
  if (k === "rules")
    return (
      <CatalogTable
        records={
          screen.path.includes("nodes")
            ? c.atomicContracts || []
            : c.rules || []
        }
      />
    );
  if (k === "lineage" && !["rules", "compiler"].includes(screen.module))
    return <RuntimePanel screen={screen} />;
  if (["graph", "lineage"].includes(k))
    return (
      <>
        <Callout title="Source-defined dependency view">
          Edges come from the frozen rule catalog. This is not a runtime
          execution trace or proof of graph closure.
        </Callout>
        <RuleGraph
          records={c.rules || []}
          onInspect={inspect}
          title={screen.title}
        />
      </>
    );
  if (k === "fields" || k === "form")
    return (
      <>
        <CaseSelector />
        {k === "form" && (
          <Callout title="No generated Form 1040">
            The field map below is the source-defined logical model. No tax
            values, rendered return, or preparation receipt exist for this local
            draft.
          </Callout>
        )}
        <FormMapView
          records={c.fields || []}
          onInspect={inspect}
          title="TY2025 Form 1040 · logical field map"
        />
      </>
    );
  if (k === "coverage")
    return (
      <>
        <CoveragePanel
          records={c.gates || []}
          onInspect={inspect}
          title="Defined controls · execution evidence required"
        />
        <CatalogTable records={c.gates || []} />
      </>
    );
  if (k === "gates")
    return (
      <CatalogTable
        records={(c.gates || []).filter((r: Row) =>
          r.source?.path.includes(
            screen.module === "validation"
              ? "2B_9_"
              : screen.module === "releases"
                ? "2B_11_"
                : "2B_12_",
          ),
        )}
      />
    );
  if (k === "tests") return <CatalogTable records={c.tests || []} />;
  if (k === "authorities")
    return <CatalogTable records={c.authorities || []} />;
  if (k === "roles" || k === "permissions")
    return (
      <CatalogTable
        records={k === "roles" ? c.roles || [] : c.permissions || []}
      />
    );
  if (k === "manifests")
    return (
      <>
        <Callout title="Source fingerprints, not sealed runtime manifests">
          These SHA-256 fingerprints identify the imported architecture files.
          They are not input/output manifest attestations or frozen authority
          evidence.
        </Callout>
        <ManifestView records={c.sourceFiles || []} onInspect={inspect} />
      </>
    );
  if (k === "admission") {
    const n = Object.keys(selected?.answers || {}).length;
    return (
      <>
        <CaseSelector />
        <div className="admission-card card">
          <span className="form-card-icon">
            <ClipboardList size={26} />
          </span>
          <Badge>Not evaluated</Badge>
          <h2>Complete facts come first.</h2>
          <p>
            {n} of {c.questions?.length || 0} source question identifiers have
            draft answers. This is capture progress only, not an admission
            determination.
          </p>
          <Progress
            value={
              c.questions?.length
                ? Math.min(100, (n / c.questions.length) * 100)
                : 0
            }
            label="Draft questionnaire capture progress"
          />
          <Button
            variant="primary"
            onClick={() => navigate("/intake/questionnaire")}
          >
            Continue questionnaire <ArrowRight size={15} />
          </Button>
        </div>
        <Callout title="Admission engine required">
          ELIGIBLE, NEEDS_FACTS, CONFLICT, and UNSUPPORTED may only be emitted
          by the qualified admission engine against complete evidence. No
          outcome is inferred here.
        </Callout>
      </>
    );
  }
  if (k === "reconciliation")
    return (
      <>
        <CaseSelector />
        <div className="comparison-grid">
          <section className="card form-card">
            <h2>Source assertions</h2>
            <p>Original captured values remain unverified drafts.</p>
            <pre className="json-view">
              {JSON.stringify(selected?.facts || {}, null, 2)}
            </pre>
          </section>
          <section className="card form-card">
            <h2>Reconciliation evidence</h2>
            <Empty
              title="No independent reconciliation"
              description="A qualified verifier must compare the source facts, evidence and resulting records. No matching result is inferred from document presence."
            />
          </section>
        </div>
      </>
    );
  if (k === "users")
    return <AdminDirectory user={session.user} notify={notify} />;
  if (k === "tenants")
    return (
      <div className="card form-card">
        <h2>{session.workspace?.name}</h2>
        <KeyValue label="Workspace identifier">
          <code>{session.workspace?.id}</code>
        </KeyValue>
        <KeyValue label="Environment">Local · Synthetic</KeyValue>
        <KeyValue label="Your role">{human(session.user.role)}</KeyValue>
        <Callout title="Isolated workspace">
          Cross-tenant administration and environment promotion require
          configured production identity and governance services.
        </Callout>
      </div>
    );
  if (k === "datasets") return <CatalogTable records={c.datasets || []} />;
  if (k === "schemas") return <CatalogTable records={c.schemas || []} />;
  if (k === "policies" || k === "checklists")
    return (
      <CatalogTable
        records={k === "checklists" ? c.gates || [] : c.policies || []}
      />
    );
  if (k === "flags" || k === "environments" || k === "settings")
    return (
      <section className="card form-card">
        <h2>Explicit capabilities</h2>
        <p>
          Actual local server capabilities. Safety-critical controls cannot be
          enabled from a visual toggle.
        </p>
        {Object.entries(w.capabilities || {}).map(([key, val]) => (
          <KeyValue key={key} label={human(capabilityLabel(key))}>
            <Badge>
              {val === true ? "Available locally" : "Not configured"}
            </Badge>
          </KeyValue>
        ))}
        <Button onClick={() => navigate("/settings/design-system")}>
          <SlidersHorizontal size={15} />
          Explore design system
        </Button>
      </section>
    );
  return <RuntimePanel screen={screen} />;
}

function AdminUsers() {
  const { inspect, session } = useWorkspace(),
    [records, setRecords] = useState<Row[]>([]),
    [error, setError] = useState("");
  useEffect(() => {
    api("/admin/users")
      .then((r) => setRecords(Array.isArray(r) ? r : r.users || []))
      .catch((e) => setError(e.message));
  }, []);
  return (
    <>
      {error && (
        <Callout title="Directory access unavailable">{taxText(error)}</Callout>
      )}
      <DataTable
        records={records}
        columns={[
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          {
            key: "role",
            label: "Workspace role",
            render: (v) => <Badge>{human(v)}</Badge>,
          },
          {
            key: "mfaEnabled",
            label: "MFA",
            render: (v) => (v ? "Enabled" : "Not enabled"),
          },
        ]}
        onInspect={inspect}
      />
    </>
  );
}
